export const PICKS_API_PRODUCTION_BASE = 'https://picks-api.shotselect.app'
export const PICKS_API_STAGING_BASE = 'https://picks-api-staging.shotselect.app'
export const PICKS_API_BETA_BASE = PICKS_API_STAGING_BASE

interface LocationLike {
  hostname: string
  origin?: string
}

/**
 * Hosts where the page itself is a local/dev surface, so a loopback API base
 * is a legitimate choice rather than a leaked build artifact.
 */
const LOCAL_PAGE_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'beta.gallery.shotselect.app',
])

function cleanBase(url: string): string {
  return url.replace(/\/+$/, '')
}

/** Does this API base point at the visitor's own machine? */
function isLoopbackBase(url: string): boolean {
  let hostname: string
  try {
    hostname = new URL(url).hostname
  } catch {
    return false
  }
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]'
  )
}

export function resolvePicksApiBase(envBase?: string, locationLike?: LocationLike): string {
  const loc =
    locationLike ??
    (typeof window !== 'undefined'
      ? { hostname: window.location.hostname, origin: window.location.origin }
      : undefined)
  const hostname = loc?.hostname ?? ''

  // The bare Pages alias serves production; only branch/preview aliases stage.
  if (hostname === 'shotselect-gallery.pages.dev') return PICKS_API_PRODUCTION_BASE
  if (hostname === 'beta.gallery.shotselect.app') return PICKS_API_BETA_BASE

  // Other *.pages.dev builds are staging/preview surfaces. Resolve from the
  // runtime hostname before considering a baked VITE_API_BASE so a stale Pages
  // environment variable cannot send staging gallery tokens to production.
  if (hostname.endsWith('.pages.dev')) {
    return PICKS_API_STAGING_BASE
  }

  const override = envBase?.trim()
  if (override) {
    // A loopback base is a local-dev artifact. Baked into a deployed build it
    // points every visitor's browser at THEIR OWN machine, so the gallery
    // dead-ends on a connection error for everyone — which is exactly how
    // gallery.shotselect.app shipped with VITE_API_BASE=http://localhost:8787
    // and answered "We couldn't load this gallery" for every client. Honour a
    // loopback override only when the page itself is local.
    if (!isLoopbackBase(override) || LOCAL_PAGE_HOSTS.has(hostname)) {
      return cleanBase(override)
    }
  }

  if (LOCAL_PAGE_HOSTS.has(hostname)) {
    return 'http://127.0.0.1:8787'
  }

  return PICKS_API_PRODUCTION_BASE
}
